/**
 * Splits line-item HTML details into a short summary (job name, size, etc.)
 * and the remainder (typically "Printing Details" onward) for expand/collapse.
 */
const splitAtPrintingDetails = (
  html: string
): { summary: string; hasMore: boolean } | null => {
  const full = String(html ?? "");
  if (!full.trim()) {
    return null;
  }
  const patterns: RegExp[] = [
    /^(.*?)(<p[^>]*>[\s\n\r]*<strong>\s*Printing Details\s*:\s*<\/strong>[\s\S]*)$/i,
    /^(.*?)(<strong>\s*Printing Details\s*:\s*<\/strong>[\s\S]*)$/i,
    /^([\s\S]*?)(\s*Printing Details\s*:[\s\S]*)$/i,
  ];
  for (const re of patterns) {
    const m = full.match(re);
    if (m?.[1] != null && m[2]?.trim()) {
      const summary = m[1].trimEnd();
      if (summary.length < full.trim().length) {
        return { summary: m[1].trimEnd(), hasMore: true };
      }
    }
  }
  return null;
};

/** Collapsed jobs-table description: Job Name and Size are the first value lines. */
const COMPACT_DETAIL_LINE_COUNT = 2;

const stripDetailMarkup = (html: string) =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const toDetailLines = (html: string): string[] => {
  const normalized = String(html ?? "")
    .replace(/<\/p>/gi, "<br/>")
    .replace(/<p[^>]*>/gi, "");

  return normalized
    .split(/<br\s*\/?>/i)
    .map((line) => line.trim())
    .filter((line) => stripDetailMarkup(line).length > 0);
};

const isValueLine = (html: string) => {
  const afterLabel = stripDetailMarkup(
    html.replace(/<strong>[\s\S]*?<\/strong>/gi, "")
  );
  return afterLabel.length > 0 || /<strong>[^<]*:[^<]*<\/strong>/i.test(html);
};

/**
 * Preview is the first value lines (typically Job Name, then Size).
 * Section titles with no value are kept in `allLines` for the expanded view.
 */
const getCompactDetailSummary = (html: string) => {
  const allLines = toDetailLines(html);
  const valueLines = allLines.filter(isValueLine);
  const source = valueLines.length > 0 ? valueLines : allLines;

  return {
    allLines,
    previewLines: source.slice(0, COMPACT_DETAIL_LINE_COUNT),
  };
};

export { splitAtPrintingDetails, getCompactDetailSummary };
